<template>
  <div class="wellness-empty">
    <!-- Icon -->
    <div class="wellness-empty__icon">
      <component :is="iconComponent" />
    </div>

    <!-- Title -->
    <h3 class="wellness-empty__title">
      {{ title }}
    </h3>

    <!-- Description -->
    <p class="wellness-empty__description">
      {{ description }}
    </p>

    <!-- Action Button -->
    <button
      v-if="actionText && onAction"
      class="wellness-empty__action"
      @click="onAction">
      {{ actionText }}
    </button>

    <!-- Secondary Link -->
    <a
      v-if="linkText && linkHref"
      :href="linkHref"
      class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mt-3">
      {{ linkText }}
    </a>
  </div>
</template>

<script setup>
import { computed, h } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'no-data',
    validator: (val) => [
      'no-data',
      'no-insights',
      'no-goals',
      'no-symptoms',
      'ai-unavailable',
      'sync-required',
      'error'
    ].includes(val)
  },
  title: {
    type: String,
    default: null
  },
  description: {
    type: String,
    default: null
  },
  actionText: {
    type: String,
    default: null
  },
  onAction: {
    type: Function,
    default: null
  },
  linkText: {
    type: String,
    default: null
  },
  linkHref: {
    type: String,
    default: null
  }
})

// Default content based on type
const defaults = {
  'no-data': {
    icon: 'heart-pulse',
    title: 'No wellness data yet',
    description: 'Connect your Apple Health or sync your device to see your mindfulness, audio exposure, and symptom data here.'
  },
  'no-insights': {
    icon: 'lightbulb',
    title: 'No insights available',
    description: 'Log more wellness data over the next few days to receive AI-powered insights and recommendations.'
  },
  'no-goals': {
    icon: 'target',
    title: 'No goals set',
    description: 'Set wellness goals to track your progress and build healthy habits.'
  },
  'no-symptoms': {
    icon: 'check-circle',
    title: 'No symptoms logged',
    description: 'Great news! You haven\'t logged any symptoms in this period.'
  },
  'ai-unavailable': {
    icon: 'cpu',
    title: 'AI temporarily unavailable',
    description: 'Our AI service is currently unavailable. Your wellness data is still being tracked and insights will be generated when the service is restored.'
  },
  'sync-required': {
    icon: 'refresh',
    title: 'Sync required',
    description: 'Open the BodyOS app on your iPhone and sync your HealthKit data to see your wellness metrics.'
  },
  'error': {
    icon: 'alert-triangle',
    title: 'Something went wrong',
    description: 'We couldn\'t load your wellness data. Please try again.'
  }
}

const title = computed(() => props.title || defaults[props.type]?.title || 'No data')
const description = computed(() => props.description || defaults[props.type]?.description || '')

// SVG icons as functional components
const icons = {
  'heart-pulse': () => h('svg', {
    class: 'w-12 h-12',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '1.5',
      d: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
    }),
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '1.5',
      d: 'M8 12h2l1-3 2 6 1-3h2'
    })
  ]),

  'lightbulb': () => h('svg', {
    class: 'w-12 h-12',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '1.5',
      d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
    })
  ]),

  'target': () => h('svg', {
    class: 'w-12 h-12',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24'
  }, [
    h('circle', { cx: '12', cy: '12', r: '10', 'stroke-width': '1.5' }),
    h('circle', { cx: '12', cy: '12', r: '6', 'stroke-width': '1.5' }),
    h('circle', { cx: '12', cy: '12', r: '2', 'stroke-width': '1.5', fill: 'currentColor' })
  ]),

  'check-circle': () => h('svg', {
    class: 'w-12 h-12',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '1.5',
      d: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
    })
  ]),

  'cpu': () => h('svg', {
    class: 'w-12 h-12',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '1.5',
      d: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z'
    })
  ]),

  'refresh': () => h('svg', {
    class: 'w-12 h-12',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '1.5',
      d: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
    })
  ]),

  'alert-triangle': () => h('svg', {
    class: 'w-12 h-12',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24'
  }, [
    h('path', {
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'stroke-width': '1.5',
      d: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
    })
  ])
}

const iconComponent = computed(() => {
  const iconKey = defaults[props.type]?.icon || 'heart-pulse'
  return icons[iconKey] || icons['heart-pulse']
})
</script>
